// Utility to extract filename and code from markdown code blocks
// Supports: ```js filename=src/App.tsx ... ``` and ```\n// filename: src/App.tsx\n...```

// Extract relative imports from code and check for missing files
export function validateImports(files: Array<{ filename: string; code: string }>): Array<{
  file: string;
  missingImport: string;
  suggestedPath: string;
  importType: 'relative' | 'alias';
}> {
  const missing: Array<{
    file: string;
    missingImport: string;
    suggestedPath: string;
    importType: 'relative' | 'alias';
  }> = [];
  const fileSet = new Set(files.map((f) => f.filename.toLowerCase()));

  // Also create a set of normalized paths (without extension, without src/ prefix)
  const normalizedPaths = new Set<string>();
  files.forEach((f) => {
    const normalized = f.filename
      .toLowerCase()
      .replace(/^src\//, '')
      .replace(/\.(tsx?|jsx?|css|json)$/, '');
    normalizedPaths.add(normalized);
  });

  // Extensions to check for resolved paths
  const extensions = ['', '.tsx', '.ts', '.jsx', '.js', '/index.tsx', '/index.ts'];

  // Helper: check if a resolved path exists in the file set
  const pathExists = (resolvedPath: string): boolean => {
    for (const ext of extensions) {
      const checkPath = (resolvedPath + ext).toLowerCase();
      if (fileSet.has(checkPath) || fileSet.has('src/' + checkPath)) {
        return true;
      }
    }
    // Also check normalized paths
    const normalizedImport = resolvedPath.toLowerCase().replace(/^src\//, '');
    return normalizedPaths.has(normalizedImport);
  };

  for (const file of files) {
    // Skip non-JS/TS files
    if (!/\.(tsx?|jsx?)$/.test(file.filename)) continue;

    // ── 1. Relative imports (./something or ../something) ──
    const importRegex = /import\s+(?:[\w{}\s,*]+\s+from\s+)?["'](\.[^"']+)["']/g;
    let match;

    while ((match = importRegex.exec(file.code)) !== null) {
      const importPath = match[1];

      // Skip non-relative imports
      if (!importPath.startsWith('./') && !importPath.startsWith('../')) continue;
      // Skip CSS/asset imports
      if (/\.(css|scss|sass|less|svg|png|jpg|jpeg|gif|webp|ico|json)$/i.test(importPath)) continue;

      // Resolve the import path relative to the file
      const fileDir = file.filename.replace(/\/[^/]+$/, '');
      let resolvedPath = importPath;

      if (importPath.startsWith('./')) {
        resolvedPath = fileDir + '/' + importPath.slice(2);
      } else if (importPath.startsWith('../')) {
        const parts = fileDir.split('/');
        let upCount = 0;
        let remaining = importPath;
        while (remaining.startsWith('../')) {
          upCount++;
          remaining = remaining.slice(3);
        }
        resolvedPath = parts.slice(0, -upCount).join('/') + '/' + remaining;
      }

      // Normalize the resolved path
      resolvedPath = resolvedPath.replace(/^\.\//, '').replace(/\/+/g, '/');

      if (!pathExists(resolvedPath)) {
        const suggestedPath =
          resolvedPath.endsWith('.tsx') || resolvedPath.endsWith('.ts')
            ? resolvedPath
            : resolvedPath + '.tsx';

        missing.push({
          file: file.filename,
          missingImport: importPath,
          suggestedPath: suggestedPath.startsWith('src/') ? suggestedPath : 'src/' + suggestedPath,
          importType: 'relative',
        });
      }
    }

    // ── 2. Alias imports (@/something) ──
    const aliasRegex = /import\s+(?:[\w{}\s,*]+\s+from\s+)?["'](@\/[^"']+)["']/g;

    while ((match = aliasRegex.exec(file.code)) !== null) {
      const importPath = match[1];
      // Skip CSS/asset imports
      if (/\.(css|scss|sass|less|svg|png|jpg|jpeg|gif|webp|ico|json)$/i.test(importPath)) continue;

      // @/components/ui/button → src/components/ui/button
      const resolvedPath = importPath.replace(/^@\//, 'src/');

      if (!pathExists(resolvedPath)) {
        const suggestedPath =
          resolvedPath.endsWith('.tsx') || resolvedPath.endsWith('.ts')
            ? resolvedPath
            : resolvedPath + '.tsx';

        missing.push({
          file: file.filename,
          missingImport: importPath,
          suggestedPath,
          importType: 'alias',
        });
      }
    }
  }

  return missing;
}

/**
 * Sanitize JS/TS/JSX/TSX code to fix common AI-generated mistakes:
 * - Remove duplicate import statements (keep first occurrence)
 * - Remove self-imports (component importing itself, e.g. About.tsx importing "./About")
 */
function sanitizeJsCode(code: string, filename: string): string {
  const lines = code.split('\n');
  const seenImports = new Set<string>();
  const seenDefaultImportNames = new Set<string>(); // Track default import identifiers to prevent redeclaration
  const result: string[] = [];

  // Derive the module's own name from its filename (e.g., "About.tsx" → "About", "index.tsx" → "")
  const baseName = filename.replace(/^.*\//, '').replace(/\.(tsx?|jsx?|mjs|cjs)$/, '');

  for (const line of lines) {
    const trimmed = line.trim();

    // Match import statements: import X from "Y", import { X } from "Y", import "Y"
    const importMatch = trimmed.match(
      /^import\s+(?:(?:type\s+)?(?:\{[^}]*\}|\*\s+as\s+\w+|\w+)(?:\s*,\s*(?:\{[^}]*\}|\*\s+as\s+\w+))?\s+from\s+)?['"]([^'"]+)['"]\s*;?\s*$/,
    );

    if (importMatch) {
      const importPath = importMatch[1];

      // Check for self-import: "./About" or "../components/About" when file is About.tsx
      if (baseName && baseName !== 'index') {
        const importBaseName = importPath.replace(/^.*\//, '').replace(/\.(tsx?|jsx?|mjs|cjs)$/, '');
        if (importBaseName === baseName) {
          // Self-import — skip it
          continue;
        }
      }

      // Deduplicate by full import line
      const normalizedImport = trimmed.replace(/\s+/g, ' ').replace(/;$/, '');
      if (seenImports.has(normalizedImport)) {
        continue;
      }
      seenImports.add(normalizedImport);

      // Deduplicate by default import name (e.g., "import Features from './pages/Features'" and
      // "import Features from './Features'" — keep the first one, skip the second)
      const defaultImportMatch = trimmed.match(/^import\s+(\w+)\s+from\s+/);
      if (defaultImportMatch) {
        const importName = defaultImportMatch[1];
        if (seenDefaultImportNames.has(importName)) {
          // Same identifier imported from a different path — skip to prevent redeclaration error
          continue;
        }
        seenDefaultImportNames.add(importName);
      }
    }

    result.push(line);
  }

  return result.join('\n');
}

/**
 * Sanitize CSS code to fix common AI-generated mistakes that cause build errors.
 * - Replaces @apply font-* with CSS font-family equivalents
 * - Strips @apply with unknown custom classes
 */
function sanitizeCssCode(code: string): string {
  // Replace @apply font-body/font-heading/font-display/font-sans/font-mono with CSS font-family
  // These always fail unless defined in tailwind.config.js
  let result = code;

  // Map common @apply font-* patterns to CSS font-family
  const fontMappings: Record<string, string> = {
    'font-body': "font-family: var(--font-body, 'Inter', system-ui, sans-serif)",
    'font-heading': "font-family: var(--font-heading, 'Inter', system-ui, sans-serif)",
    'font-display': "font-family: var(--font-display, 'Inter', system-ui, sans-serif)",
    'font-sans': 'font-family: system-ui, -apple-system, sans-serif',
    'font-mono': "font-family: 'JetBrains Mono', 'Fira Code', monospace",
  };

  // Replace @apply lines that contain font-* classes
  result = result.replace(/@apply\s+([^;{}\n]+);?/g, (match, classes: string) => {
    const classList = classes.trim().split(/\s+/);
    const fontClasses = classList.filter((c: string) => fontMappings[c]);
    const otherClasses = classList.filter((c: string) => !fontMappings[c]);

    const parts: string[] = [];
    // Convert font classes to CSS font-family
    if (fontClasses.length > 0) {
      // Use the first font mapping found
      parts.push(fontMappings[fontClasses[0]] + ';');
    }
    // Keep remaining @apply classes if any
    if (otherClasses.length > 0) {
      parts.push(`@apply ${otherClasses.join(' ')};`);
    }
    return parts.join('\n  ') || match;
  });

  return result;
}

export function extractFilesFromMarkdown(
  markdown: string,
): Array<{ filename: string; code: string }> {
  const results: Array<{ filename: string; code: string }> = [];

  // Find all code blocks - support both filename="path" and filename='path' and filename=path
  const codeBlockRegex = /```([\w-]*)\s*(filename\s*=\s*["']?([^"'\s]+)["']?)?\n([\s\S]*?)```/g;
  let match;
  let lastFilename: string | null = null;

  // Preprocess: find '**File:** `filename`' before code blocks
  const fileLabelRegex = /\*\*File:\*\*\s*`([^`]+)`/g;
  const fileLabels: { index: number; filename: string }[] = [];
  let fileLabelMatch;
  while ((fileLabelMatch = fileLabelRegex.exec(markdown)) !== null) {
    fileLabels.push({ index: fileLabelMatch.index, filename: fileLabelMatch[1] });
  }

  while ((match = codeBlockRegex.exec(markdown)) !== null) {
    let filename = (match[3] || '').trim().replace(/^["']|["']$/g, ''); // Remove quotes if present
    let code = match[4];
    // Strip doubled code fence headers: AI sometimes outputs ```tsx filename="x"```tsx filename="x"
    // The regex captures the content between the doubled fences as "code" which is just the repeated header
    if (code && /^[\w-]*\s*filename\s*=/.test(code.trim())) {
      // This "code" is actually a repeated code fence header — skip this match
      continue;
    }
    // If filename not found in info string, check for // filename: ...
    if (!filename) {
      const firstLine = code.split('\n')[0];
      const fileLineMatch = firstLine.match(/^\s*\/\/\s*filename:\s*(.+)$/i);
      if (fileLineMatch) {
        filename = fileLineMatch[1].trim();
        code = code.split('\n').slice(1).join('\n');
      }
    }
    // If still no filename, check for '**File:** `filename`' before this code block
    if (!filename) {
      // Find the closest file label before this code block
      const blockStart = match.index;
      const priorLabels = fileLabels.filter((l) => l.index < blockStart);
      if (priorLabels.length > 0) {
        filename = priorLabels[priorLabels.length - 1].filename;
      }
    }
    // If still no filename, infer from language
    if (!filename) {
      const lang = (match[1] || '').toLowerCase();
      if (lang === 'html') filename = 'index.html';
      else if (lang === 'js' || lang === 'javascript') filename = 'App.js';
      else if (lang === 'ts' || lang === 'typescript') filename = 'App.ts';
      else if (lang === 'tsx') filename = 'App.tsx';
      else if (lang === 'jsx') filename = 'App.jsx';
    }
    if (filename) {
      // Normalize: backslashes to forward slashes, collapse duplicates, strip leading ./ and /
      filename = filename
        .replace(/\\/g, '/')
        .replace(/\/+/g, '/')
        .replace(/^\.\//, '')
        .replace(/^\//, '');
      // Sanitize CSS files to fix common AI-generated @apply errors
      const isCss = /\.(css|scss|less)$/i.test(filename);
      results.push({ filename, code: isCss ? sanitizeCssCode(code) : code });
    }
  }

  // Optionally, support triple single quote blocks (''')
  const singleQuoteBlockRegex =
    /'''([\w-]*)\s*(filename\s*=\s*["']?([^"'\s]+)["']?)?\n([\s\S]*?)'''/g;
  while ((match = singleQuoteBlockRegex.exec(markdown)) !== null) {
    let filename = (match[3] || '').trim().replace(/^["']|["']$/g, ''); // Remove quotes if present
    let code = match[4];
    if (!filename) {
      const firstLine = code.split('\n')[0];
      const fileLineMatch = firstLine.match(/^\s*\/\/\s*filename:\s*(.+)$/i);
      if (fileLineMatch) {
        filename = fileLineMatch[1].trim();
        code = code.split('\n').slice(1).join('\n');
      }
    }
    // If still no filename, check for '**File:** `filename`' before this code block
    if (!filename) {
      const blockStart = match.index;
      const priorLabels = fileLabels.filter((l) => l.index < blockStart);
      if (priorLabels.length > 0) {
        filename = priorLabels[priorLabels.length - 1].filename;
      }
    }
    // If still no filename, infer from language
    if (!filename) {
      const lang = (match[1] || '').toLowerCase();
      if (lang === 'html') filename = 'index.html';
      else if (lang === 'js' || lang === 'javascript') filename = 'App.js';
      else if (lang === 'ts' || lang === 'typescript') filename = 'App.ts';
      else if (lang === 'tsx') filename = 'App.tsx';
      else if (lang === 'jsx') filename = 'App.jsx';
    }
    if (filename) {
      // Normalize: backslashes to forward slashes, collapse duplicates, strip leading ./ and /
      filename = filename
        .replace(/\\/g, '/')
        .replace(/\/+/g, '/')
        .replace(/^\.\//, '')
        .replace(/^\//, '');
      const isCss = /\.(css|scss|less)$/i.test(filename);
      results.push({ filename, code: isCss ? sanitizeCssCode(code) : code });
    }
  }

  // Post-process: auto-fix common AI-generated issues
  for (const file of results) {
    if (/\.css$/i.test(file.filename)) {
      file.code = sanitizeCss(file.code);
    } else if (/\.(tsx?|jsx?|mjs|cjs)$/i.test(file.filename)) {
      file.code = sanitizeJsCode(file.code, file.filename);
    }
  }

  // Auto-heal missing component references: if a .tsx/.jsx imports ./ComponentX
  // but no ComponentX file exists in results, skip (Vite will error, but that's
  // better than silently generating broken code)

  // Auto-inject src/lib/utils.ts when any file imports @/lib/utils but it's not
  // in the generated output — this is the #1 cause of "Failed to resolve import"
  const hasAliasUtilsImport = results.some(
    (f) => f.code.includes('@/lib/utils') || f.code.includes('@/lib/utils'),
  );
  const hasUtilsFile = results.some(
    (f) => f.filename === 'src/lib/utils.ts' || f.filename === 'lib/utils.ts',
  );
  if (hasAliasUtilsImport && !hasUtilsFile) {
    results.push({
      filename: 'src/lib/utils.ts',
      code: 'import { type ClassValue, clsx } from "clsx";\nimport { twMerge } from "tailwind-merge";\n\nexport function cn(...inputs: ClassValue[]) {\n  return twMerge(clsx(inputs));\n}\n',
    });
  }

  return results;
}

/**
 * Auto-fix common AI-generated CSS issues that break Tailwind/Vite builds.
 * - Replaces @apply with undefined custom classes (font-body, font-heading, etc.)
 *   with equivalent plain CSS declarations
 * - Strips @apply lines that reference non-standard utilities
 */
function sanitizeCss(css: string): string {
  // Map of commonly misused @apply classes → equivalent CSS
  const customApplyMap: Record<string, string> = {
    'font-body': "font-family: var(--font-body, 'Inter', system-ui, sans-serif);",
    'font-heading': "font-family: var(--font-heading, 'Inter', system-ui, sans-serif);",
    'font-display': "font-family: var(--font-display, 'Inter', system-ui, sans-serif);",
    'font-mono': "font-family: var(--font-mono, 'JetBrains Mono', monospace);",
    'font-sans': 'font-family: var(--font-sans, system-ui, sans-serif);',
    'font-serif': 'font-family: var(--font-serif, Georgia, serif);',
  };

  // Built-in Tailwind utilities that are safe to @apply (common prefixes)
  const safeApplyPrefixes = [
    'flex',
    'grid',
    'block',
    'inline',
    'hidden',
    'absolute',
    'relative',
    'fixed',
    'sticky',
    'bg-',
    'text-',
    'border-',
    'rounded',
    'p-',
    'px-',
    'py-',
    'pt-',
    'pb-',
    'pl-',
    'pr-',
    'm-',
    'mx-',
    'my-',
    'mt-',
    'mb-',
    'ml-',
    'mr-',
    'w-',
    'h-',
    'min-',
    'max-',
    'top-',
    'right-',
    'bottom-',
    'left-',
    'inset-',
    'start-',
    'end-',
    'gap-',
    'space-',
    'space-x-',
    'space-y-',
    'items-',
    'justify-',
    'self-',
    'overflow',
    'z-',
    'opacity-',
    'shadow',
    'ring',
    'transition',
    'duration-',
    'ease-',
    'delay-',
    'animate-',
    'cursor-',
    'select-',
    'list-',
    'decoration-',
    'underline',
    'line-through',
    'italic',
    'not-italic',
    'font-thin',
    'font-light',
    'font-normal',
    'font-medium',
    'font-semibold',
    'font-bold',
    'font-extrabold',
    'font-black',
    'text-xs',
    'text-sm',
    'text-base',
    'text-lg',
    'text-xl',
    'text-2xl',
    'text-3xl',
    'text-4xl',
    'text-5xl',
    'text-6xl',
    'text-7xl',
    'text-8xl',
    'text-9xl',
    'leading-',
    'tracking-',
    'whitespace-',
    'break-',
    'truncate',
    'object-',
    'aspect-',
    'columns-',
    'col-',
    'row-',
    'float-',
    'clear-',
    'table',
    'caption-',
    'sr-only',
    'not-sr-only',
    'pointer-events-',
    'resize',
    'scroll-',
    'snap-',
    'touch-',
    'will-change-',
    'fill-',
    'stroke-',
    'accent-',
    'caret-',
    'outline',
    'placeholder-',
    'backdrop-',
    'filter',
    'blur-',
    'brightness-',
    'contrast-',
    'grayscale',
    'hue-rotate-',
    'invert',
    'saturate-',
    'sepia',
    'drop-shadow',
    'transform',
    'rotate-',
    'scale-',
    'skew-',
    'translate-',
    'origin-',
    'place-',
    'content-',
    'order-',
    'grow',
    'shrink',
    'basis-',
    'size-',
    'line-clamp-',
    'divide-',
    'from-',
    'via-',
    'to-',
    'indent-',
    'container',
    'prose',
    'sr-',
    'dark:',
    'hover:',
    'focus:',
    'active:',
    'group-hover:',
    'group-focus:',
    'focus-within:',
    'focus-visible:',
    'disabled:',
    'first:',
    'last:',
    'odd:',
    'even:',
    'placeholder:',
    'sm:',
    'md:',
    'lg:',
    'xl:',
    '2xl:',
  ];

  return css.replace(/@apply\s+([^;}\n]+)/g, (fullMatch, classesStr: string) => {
    const classes = classesStr.trim().split(/\s+/);
    const safeClasses: string[] = [];
    const replacements: string[] = [];

    for (const cls of classes) {
      if (customApplyMap[cls]) {
        replacements.push(customApplyMap[cls]);
      } else if (
        safeApplyPrefixes.some(
          (prefix) => cls.startsWith(prefix) || cls === prefix.replace(/-$/, ''),
        )
      ) {
        safeClasses.push(cls);
      } else {
        // Unknown class - keep it (let Tailwind/Vite handle it rather than stripping valid utilities)
        safeClasses.push(cls);
      }
    }

    const parts: string[] = [];
    if (safeClasses.length > 0) {
      parts.push(`@apply ${safeClasses.join(' ')}`);
    }
    parts.push(...replacements);

    return parts.length > 0 ? parts.join(';\n    ') : '/* removed: ' + fullMatch + ' */';
  });
}
